import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, XCircle, Send } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export const ReplacementNote = ({ at, reason, testId }) => at ? (
  <span className="text-[10px] text-amber-300" data-testid={testId}>Replacement requested on {fmtDate(at)}{reason ? ` — ${reason}` : ""}.</span>
) : null;

export const RejectedNote = ({ at, reason, testId }) => at ? (
  <span className="text-[10px] text-red-300" data-testid={testId}>Rejected on {fmtDate(at)}{reason ? ` — ${reason}` : ""}.</span>
) : null;

export const RejectRequestButton = ({ label, onConfirm, className = "", testId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); setOpen(false); setReason(""); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className || "flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"} data-testid={testId}>
        <XCircle size={11} /> Reject
      </button>
      {open && (
        <div data-lenis-prevent className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)} data-testid={`${testId}-confirm`}>
          <div className="bg-nx-navy border border-white/15 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] tracking-[0.24em] uppercase text-red-300 mb-1">Reject document</p>
            <p className="text-white font-medium" data-testid={`${testId}-doc-name`}>{label}</p>
            <label className="block text-[10px] tracking-[0.16em] uppercase text-slate-500 mt-4 mb-1.5">Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={800} autoFocus
              placeholder="Explain exactly what was wrong and what the candidate must resubmit"
              className="w-full bg-white/[0.04] border border-white/15 focus:border-nx-gold/60 outline-none text-sm text-white px-3 py-2.5 resize-y" data-testid={`${testId}-reason`} />
            <p className="text-xs text-slate-500 mt-2">The candidate is emailed this reason and can re-upload only this document.</p>
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className="text-xs tracking-[0.14em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid={`${testId}-cancel`}>Cancel</button>
              <button type="button" onClick={confirm} disabled={busy || !reason.trim()} className="bg-red-600 hover:bg-red-500 text-white text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid={`${testId}-ok`}>
                {busy && <Loader2 size={12} className="animate-spin" />} Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ReasonDialog = ({ open, busy, onClose, eyebrow, eyebrowCls, label, placeholder, hint, reason, setReason, onConfirm, testId, confirmCls, confirmText }) => open ? (
  <div data-lenis-prevent className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid={`${testId}-confirm`}>
    <div className="bg-nx-navy border border-white/15 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
      <p className={`text-[10px] tracking-[0.24em] uppercase mb-1 ${eyebrowCls}`}>{eyebrow}</p>
      <p className="text-white font-medium" data-testid={`${testId}-doc-name`}>{label}</p>
      <label className="block text-[10px] tracking-[0.16em] uppercase text-slate-500 mt-4 mb-1.5">Reason <span className="text-red-300">*</span></label>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} maxLength={800} autoFocus placeholder={placeholder}
        className="w-full bg-white/[0.04] border border-white/15 focus:border-nx-gold/60 outline-none text-sm text-white px-3 py-2.5 resize-y" data-testid={`${testId}-reason`} />
      <p className="text-xs text-slate-500 mt-2">{hint}</p>
      <div className="flex justify-end gap-3 mt-5">
        <button type="button" onClick={onClose} disabled={busy} className="text-xs tracking-[0.14em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid={`${testId}-cancel`}>Cancel</button>
        <button type="button" onClick={onConfirm} disabled={busy || !reason.trim()} className={`${confirmCls} text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-50 flex items-center gap-2`} data-testid={`${testId}-ok`}>
          {busy && <Loader2 size={12} className="animate-spin" />} {confirmText}
        </button>
      </div>
    </div>
  </div>
) : null;

export const ReplaceRequestButton = ({ label, onConfirm, className = "", testId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); setOpen(false); setReason(""); } catch (_) { /* toast shown by caller */ } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className || "flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors"} data-testid={testId}>
        <RefreshCw size={11} /> Replace
      </button>
      <ReasonDialog open={open} busy={busy} onClose={() => setOpen(false)} eyebrow="Replacement request" eyebrowCls="text-amber-300" label={label}
        placeholder="Why must this document be replaced? (e.g. scan is blurred / wrong document uploaded / name mismatch)"
        hint="An HR Department email with this reason and a secure re-upload link is sent immediately. The reason is recorded in the record's timeline. Status returns to Pending."
        reason={reason} setReason={setReason} onConfirm={confirm} testId={testId} confirmCls="bg-nx-gold hover:bg-nx-goldlight text-nx-navy" confirmText="Send Request" />
    </>
  );
};

export const RequestDocButton = ({ label, onConfirm, resend = false, disabled = false, hint, testId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); setOpen(false); setReason(""); } catch (_) { /* toast shown by caller */ } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} disabled={disabled} className={`flex items-center gap-1 text-[10px] uppercase px-2.5 py-1.5 border transition-colors ${resend ? "border-amber-500/30 text-amber-200/70 hover:bg-amber-500/10" : "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"}`} data-testid={testId} title={resend ? "Already requested — click to resend the request email" : "Email an upload link for this document"}>
        <Send size={11} /> {resend ? "Resend Request" : "Request Document"}
      </button>
      <ReasonDialog open={open} busy={busy} onClose={() => setOpen(false)} eyebrow="Request document" eyebrowCls="text-amber-300" label={label}
        placeholder="Why is this document needed? (e.g. missing from your employee file / required for PF enrolment)"
        hint={hint || "An HR Department email with this reason and a secure upload link is sent immediately. Both CEO Telegram accounts are notified and the reason is recorded in the timeline."}
        reason={reason} setReason={setReason} onConfirm={confirm} testId={testId} confirmCls="bg-nx-gold hover:bg-nx-goldlight text-nx-navy" confirmText="Send Request" />
    </>
  );
};

export const RequestMissingButton = ({ api, letterId, employeeName, onDone }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({});
  const [picked, setPicked] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setOpen(true);
    setItems(null);
    try {
      const { data } = await api.get(`/candidate-review/admin/${letterId}/missing`);
      setItems(data.items);
      setMeta({ window: data.window, kind: data.kind });
      const init = {};
      data.items.forEach((it) => { init[it.id] = it.required && !it.requested; });
      setPicked(init);
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not load the missing items.");
      setOpen(false);
    }
  };
  const chosen = Object.keys(picked).filter((k) => picked[k]);
  const submit = async () => {
    if (!reason.trim() || !chosen.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/candidate-review/admin/${letterId}/request-missing`, { item_ids: chosen, reason: reason.trim() });
      const t = data.telegram;
      toast.success(`${data.requested.length} item${data.requested.length === 1 ? "" : "s"} requested in one email to ${data.doc_request?.sent_to} · link valid ${data.doc_request?.window}${t?.ok ? ` · CEO informed on Telegram (${t.sent.length} chats)` : ""}`);
      if (t && !t.ok) toast.warning("Telegram confirmation NOT delivered to the CEO — check CEO_TELEGRAM_CHAT_IDS.", { duration: 9000 });
      setOpen(false); setReason("");
      onDone?.();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Could not send the request.");
    } finally { setBusy(false); }
  };
  const folders = items ? [...new Set(items.map((i) => i.folder))] : [];
  return (
    <>
      <button type="button" onClick={start} className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition-colors" data-testid="chk-request-missing" title="One email covering every empty slot">
        <Send size={12} /> Request All Missing
      </button>
      {open && (
        <div data-lenis-prevent className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)} data-testid="chk-request-missing-confirm">
          <div className="bg-nx-navy border border-white/15 w-full max-w-2xl p-5 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] tracking-[0.24em] uppercase mb-1 text-amber-300">Request all missing items</p>
            <p className="text-white font-medium">{employeeName}</p>
            <p className="text-xs text-slate-400 mt-1">One HR Department email listing every ticked item, with a single secure link valid {meta.window || "…"} ({meta.kind === "staff" ? "joined-staff rule" : "candidate rule"}). Mandatory items are pre-ticked; tick any "as applicable" ones you also need.</p>
            <div className="mt-4 overflow-y-auto pr-1 space-y-3 min-h-[80px]" data-testid="chk-request-missing-list">
              {!items && <p className="text-xs text-slate-500">Loading…</p>}
              {items && items.length === 0 && <p className="text-xs text-emerald-300">Nothing is missing — every slot is filled.</p>}
              {folders.map((f) => (
                <div key={f}>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-1">{f}</p>
                  <div className="grid sm:grid-cols-2 gap-1">
                    {items.filter((i) => i.folder === f).map((it) => (
                      <label key={it.id} className={`flex items-start gap-2 text-xs px-2.5 py-2 border cursor-pointer ${picked[it.id] ? "border-amber-500/50 bg-amber-500/5 text-white" : "border-white/10 text-slate-300 hover:border-white/25"}`} data-testid={`chk-missing-${it.id}`}>
                        <input type="checkbox" checked={!!picked[it.id]} onChange={(e) => setPicked((p) => ({ ...p, [it.id]: e.target.checked }))} className="mt-0.5 accent-amber-400" />
                        <span className="min-w-0">
                          <span className="block leading-snug">{it.label}</span>
                          <span className="block text-[10px] text-slate-500">{it.type === "text" ? "detail" : "document"} · {it.required ? "mandatory" : "as applicable"}{it.requested ? " · already requested" : ""}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <label className="block text-[10px] tracking-[0.16em] uppercase text-slate-500 mt-4 mb-1.5">Reason <span className="text-red-300">*</span></label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={800} placeholder="Why are these items needed? (e.g. completing your employee file before payroll)"
              className="w-full bg-white/[0.04] border border-white/15 focus:border-nx-gold/60 outline-none text-sm text-white px-3 py-2.5 resize-y" data-testid="chk-request-missing-reason" />
            <div className="flex items-center justify-between gap-3 mt-4">
              <span className="text-xs text-slate-400" data-testid="chk-request-missing-count">{chosen.length} item{chosen.length === 1 ? "" : "s"} selected</span>
              <div className="flex gap-3">
                <button type="button" onClick={() => setOpen(false)} disabled={busy} className="text-xs tracking-[0.14em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid="chk-request-missing-cancel">Cancel</button>
                <button type="button" onClick={submit} disabled={busy || !reason.trim() || !chosen.length} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="chk-request-missing-ok">
                  {busy && <Loader2 size={12} className="animate-spin" />} Send One Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
